import {
    ComponentGeneralDetail,
    ComponentGeneralOrdinalMedia,
    Enum_Componentgeneraldetail_Viewvariant
} from "@/app/lib/generated/graphql-types";
import { DetailSplitContentWrapperFrame } from "@/app/features/shared/detail-section/ui/shared/DetailSectionFrames";
import { alpha, Box, Stack, Typography } from "@mui/material";
import ErrorBox from "@/app/__to_be_integrated/_____components/_NEW/ErrorBox";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";
import { Markdown } from "@/app/features/shared/markdown/Markdown";
import { DetailsMenuAlignment } from "@/app/features/shared/detail-section/types/types";
import { positionForDetailsMenuAlignment } from "@/app/features/shared/detail-section/helper/helper";
import Grid from "@mui/material/Grid2";
import Section from "@/app/features/shared/snap-section/Section";

export interface DetailSectionPublicProps {
    data: ComponentGeneralDetail;
    alignment: DetailsMenuAlignment;
}

export default function DetailSectionPublic(props: DetailSectionPublicProps) {
    switch (props.data.viewVariant) {
        case Enum_Componentgeneraldetail_Viewvariant.SplitContent:
            return <SplitContentVariant {...props} />;
        case Enum_Componentgeneraldetail_Viewvariant.Documents:
            return <DocumentsVariant {...props} />;
        case Enum_Componentgeneraldetail_Viewvariant.DocumentsMinimal:
            return <DocumentsMinimalVariant {...props} />;
        default:
            return <ErrorBox error="NOT YET IMPLEMENTED" />;
    }
}

function SplitContentVariant({ data, alignment }: DetailSectionPublicProps) {
    const title = data.title?.trim();
    const subtitle = data.subtitle?.trim();

    return (
        <DetailSplitContentWrapperFrame
            alignment={alignment}
            maxSplitPosition={data.splitMax || data.splitInitial || 50}
            initialSplitPosition={data.splitInitial || 50}
            centerContentFromMiddle
            content={
                <>
                    {title && (
                        <Typography
                            variant="h1"
                            component="h2"
                            color="primary"
                        >
                            {title}
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography
                            component="h2"
                            variant="subtitle1"
                            color="text.secondary"
                        >
                            {subtitle}
                        </Typography>
                    )}
                    {data.description && (
                        <Markdown
                            value={data.description}
                            typographyOverrides={{
                                paragraph: 'body2'
                            }}
                        />
                    )}
                </>
            }
            background={
                <MediaView
                    src={data.image || PLACEHOLDER_IMAGE}
                    magnifier="image"
                    infoViewType="image"
                    detailsMenuPosition={positionForDetailsMenuAlignment(alignment)}
                    showDetailsMenu
                    fill
                />
            }
        />
    )
}

function DocumentsVariant({ data, alignment }: DetailSectionPublicProps) {
    const documents = getSortedDocuments(data);
    const title = data.title?.trim();
    const subtitle = data.subtitle?.trim();

    return (
        <Section className="relative">
            <Box sx={{ display: 'flex', flex: '1 1 auto', alignItems: 'flex-end' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    <MediaView
                        src={data.image || PLACEHOLDER_IMAGE}
                        width={1080}
                        height={900}
                        slots={{
                            root: {
                                sx: {
                                    height: '100%',
                                }
                            },
                            image: {
                                style: {
                                    width: '100%',
                                    objectFit: 'cover',
                                    objectPosition: data.imagePosition || 'center',
                                }
                            }
                        }}
                        magnifier="image"
                        detailsMenuPosition={positionForDetailsMenuAlignment(alignment)}
                        showDetailsMenu
                    />
                </Box>
                <Stack
                    spacing={4}
                    alignItems="center"
                    sx={{
                        display: 'flex',
                        minWidth: '100%',
                        px: '10%',
                        pt: 4,
                        pb: 8,
                        zIndex: 10,
                        borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, .5)}`,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, .75),
                    }}
                >
                    {(title || subtitle) && (
                        <Stack spacing={1} alignItems="center">
                            {title && (
                                <Typography variant="h2" component="h2" color="primary">
                                    {title}
                                </Typography>
                            )}
                            {subtitle && (
                                <Typography variant="h5" component="h3" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            )}
                        </Stack>
                    )}
                    {documents.length > 0 && (
                        <Grid
                            container
                            spacing={2}
                            columns={5}
                            sx={{
                                display: 'flex',
                                flex: '1 1 auto',
                                height: '100%',
                                minWidth: 'inherit',
                            }}
                        >
                            {documents.map((document, index) => (
                                <Grid key={document.id ?? index} size={1}>
                                    <MediaView src={document.media ?? PLACEHOLDER_IMAGE} width={300} height={400} />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Stack>
            </Box>
        </Section>
    )
}

function DocumentsMinimalVariant({ data }: DetailSectionPublicProps) {
    const documents = getSortedDocuments(data);
    const title = data.title?.trim();
    const subtitle = data.subtitle?.trim();

    return (
        <Section
            className="relative"
            style={{ height: 'calc(100svh - var(--header-height, 138px))' }}
        >
            <Stack
                spacing={8}
                alignItems="center"
                justifyContent="center"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: '100%',
                    height: 'calc(100svh - var(--header-height, 138px))',
                    maxHeight: 'calc(100svh - var(--header-height, 138px))',
                    minHeight: 0,
                    px: '10%',
                    pt: 4,
                    pb: 8,
                    zIndex: 10,
                    overflow: 'hidden',
                }}
            >
                {(title || subtitle) && (
                    <Stack
                        spacing={1}
                        alignItems="center"
                        sx={{
                            flex: '0 1 auto',
                            maxWidth: '100%',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {title && (
                            <Typography variant="h2" component="h2" color="primary">
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography variant="h5" component="h3" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Stack>
                )}

                {documents.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flex: '1 10 auto',
                            width: '100%',
                            minWidth: 0,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                height: '100%',
                                width: '100%',
                                minWidth: 0,
                                minHeight: 0,
                                gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                                gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                                gap: 2,
                                gridAutoFlow: 'row dense',
                                alignContent: 'center',
                                '& > *': { gridColumn: 'span 2' },
                                '& > *:nth-of-type(5n+4), & > *:nth-of-type(5n+5)': {
                                    gridColumn: 'span 3',
                                },
                            }}
                        >
                            {documents.map((document, index) => (
                                <Box key={document.id ?? index} sx={{ width: '100%', minHeight: 0, overflow: 'hidden' }}>
                                    <MediaView
                                        src={document.media ?? PLACEHOLDER_IMAGE}
                                        fill
                                        objectFit="cover"
                                        slots={{
                                            root: {
                                                sx: {
                                                    height: '100%',
                                                    minHeight: 0,
                                                }
                                            },
                                            inner: {
                                                sx: {
                                                    height: '100%',
                                                    minHeight: 0,
                                                }
                                            },
                                            video: {
                                                autoPlay: true,
                                                loop: true,
                                                style: {
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
                {data.description && (
                    <Box
                        sx={{
                            flex: '0 1 auto',
                            width: '100%',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Markdown
                            value={data.description}
                            slots={{
                                root: {
                                    sx: {
                                        textAlign: 'center'
                                    }
                                }
                            }}
                        />
                    </Box>
                )}
            </Stack>
        </Section>
    )
}

function getSortedDocuments(data: ComponentGeneralDetail): ComponentGeneralOrdinalMedia[] {
    return [...(data.documents ?? [])]
        .filter((document): document is ComponentGeneralOrdinalMedia => !!document)
        .sort((d1, d2) => d2.order - d1.order);
}
